import React, { useState, useEffect, useRef } from 'react';
import { w3cwebsocket as Socket } from 'websocket';
import useAuth from '../Hooks/useAuth';
import useApiCalls from '../Hooks/useApiCalls';
import { getSegment, getLatestSegmentId, profilePicture } from '../Shared/helper';
import { useNavigate, useLocation } from 'react-router-dom';
import { NotificationManagerSuccess, NotificationManagerError } from '../utils/Notification';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faVolumeUp, faVolumeMute } from '@fortawesome/free-solid-svg-icons';

const ChatRight = ({ getChatHistory, chatId, setChatId, chatSegmentId, setChatSegmentId }) => {
  const authCtx = useAuth();
  const api = useApiCalls();
  const navigate = useNavigate();

  /* ---------- state ---------- */
  const [myMessage, setMyMessage] = useState('');
  const [messages, setMessages] = useState([]);
  const [client, setClient] = useState(null);
  const location = useLocation();
  const chatContainerRef = useRef(null);
  const [dots, setDots] = useState(false);
  const [fetchTrigger, setFetchTrigger] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [isdailyLimit, setIsdailyLimit] = useState(false);
  const currentStreamingIdRef = useRef(null);

  /* ---------- ✨ intro logic ---------- */
  const [isIntroPlaying, setIsIntroPlaying] = useState(false);
  const [introPlayedForThisChat, setIntroPlayedForThisChat] = useState(false);

  /* ---------- speech ---------- */
  const recognition = new (window.SpeechRecognition || window.webkitSpeechRecognition)();
  recognition.lang = 'en-US';
  recognition.interimResults = false;
  const [isListening, setIsListening] = useState(false);
  const [speechEnabled, setSpeechEnabled] = useState(false);

  const VolumeIcon = ({ isMuted }) => (
    <FontAwesomeIcon icon={isMuted ? faVolumeMute : faVolumeUp} />
  );

  recognition.onresult = (event) => {
    const transcript = event.results[0][0].transcript;
    setMessages((prev) => [...prev, { role: 'user', content: transcript }]);
    sendRecognizedMessage(transcript);
  };

  const sendRecognizedMessage = (message) => {
    if (isdailyLimit || isSending) return;

    console.log('Sending recognized message:', message);
    setIsSending(true);
    setDots(true);

    if (client && client.readyState === WebSocket.OPEN) {
      client.send(
        JSON.stringify({
          type: 'postChat',
          userId: authCtx.user.userId,
          chatId,
          chatSegmentId,
          userInput: message,
        })
      );
    } else {
      console.error('WebSocket not open.');
      setIsSending(false);
    }
  };

  recognition.onspeechend = () => {
    setIsListening(false);
    recognition.stop();
  };
  recognition.onerror = () => setIsListening(false);
  const toggleListening = () =>
    isListening ? recognition.stop() || setIsListening(false) : recognition.start() || setIsListening(true);
  const toggleSpeech = () => setSpeechEnabled(!speechEnabled);

  /* ---------- audio ---------- */
  const audioContext = new (window.AudioContext || window.AudioContext)();
  let source = null;
  let audioBufferQueue = [];
  let isPlaying = false;
  const bufferThreshold = 1;
  let totalBufferedDuration = 0;

  const playAudio = (arrayBuffer) => {
    audioContext.decodeAudioData(
      arrayBuffer,
      (buffer) => {
        audioBufferQueue.push(buffer);
        totalBufferedDuration += buffer.duration;
        if (!isPlaying && totalBufferedDuration >= bufferThreshold) {
          isPlaying = true;
          playNextBuffer();
        }
      },
      (e) => console.error('decode error', e)
    );
  };

  const playNextBuffer = () => {
    if (audioBufferQueue.length) {
      const buffer = audioBufferQueue.shift();
      source = audioContext.createBufferSource();
      source.buffer = buffer;
      source.connect(audioContext.destination);
      source.start();
      totalBufferedDuration -= buffer.duration;
      source.onended = playNextBuffer;
    } else {
      isPlaying = false;
    }
  };

  /* ---------- scroll ---------- */
  useEffect(() => {
    chatContainerRef.current?.scrollTo(0, chatContainerRef.current.scrollHeight);
  }, [messages]);

  /* ---------- ✨ fixed intro animation ---------- */
  const displayIntroductoryMessages = async (userName) => {
    setIsIntroPlaying(true);
    const me = userName || 'there';

    const introLines = [
      `Hi ${me}, I'm your AI Therapist. I'm here to help you work through whatever is on your mind.`,
      "In our first session, I'll ask you a few questions so I can guide you as best I can. This is your space—feel free to take it in any direction you'd like.",
      'So let\'s begin. What brings you here today?',
    ];

    for (let lineIdx = 0; lineIdx < introLines.length; lineIdx++) {
      /* 1️⃣  show typing indicator first */
      setDots(true);
      await new Promise((r) => setTimeout(r, 600)); // small thinking pause
      setDots(false);

      /* 2️⃣  now create the real message row (starts empty) */
      const messageId = `intro-${Date.now()}-${lineIdx}`;
      setMessages((prev) => [
        ...prev,
        { role: 'assistant', content: '', id: messageId, isIntro: true },
      ]);

      /* 3️⃣  type it out */
      const full = introLines[lineIdx];
      for (let c = 0; c < full.length; c++) {
        await new Promise((r) => setTimeout(r, 30));
        setMessages((prev) =>
          prev.map((m) =>
            m.id === messageId ? { ...m, content: full.slice(0, c + 1) } : m
          )
        );
      }

      /* 4️⃣  brief pause before next line */
      if (lineIdx < introLines.length - 1) {
        await new Promise((r) => setTimeout(r, 400));
      }
    }

    setIsIntroPlaying(false);
    setIntroPlayedForThisChat(true);
  };

  /* ---------- helpers & effects (unchanged except intro) ---------- */
  const fetchChatDataForId = async (idToFetch) => {
    if (!idToFetch) {
      setMessages((p) => p.filter((m) => m.isIntro));
      if (!isIntroPlaying) setMessages([]);
      return;
    }
    try {
      const res = await api(getSegment + idToFetch, 'get', {});
      const fetched = res.data;

      const preserveOptimistic =
        Array.isArray(fetched) &&
        fetched.length === 0 &&
        messages.length === 1 &&
        messages[0].role === 'user' &&
        !messages[0].isIntro &&
        chatId === idToFetch;

      if (!preserveOptimistic) setMessages(fetched.filter((m) => !m.isIntro));

      if (chatId !== idToFetch) setChatId(idToFetch);
      const latest = await api(getLatestSegmentId + idToFetch, 'get', {});
      if (latest.data?.latestSegmentId && chatSegmentId !== latest.data.latestSegmentId) {
        setChatSegmentId(latest.data.latestSegmentId);
      }
    } catch (e) {
      console.error('fetch error', e);
      if (!isIntroPlaying) setMessages([]);
      NotificationManagerError('Failed to load chat messages.');
    }
  };

  /* reset intro flag on route change */
  useEffect(() => setIntroPlayedForThisChat(false), [location.search]);

  /* decide intro vs fetch */
  useEffect(() => {
    const currentUrlId = new URLSearchParams(location.search).get('id');
    const noId = !currentUrlId;

    if (noId) {
      if (!authCtx.user) return; // wait
      if (!introPlayedForThisChat && !isIntroPlaying) {
        setMessages([]);
        setChatId(null);
        setChatSegmentId(null);
        displayIntroductoryMessages(authCtx.user?.name || authCtx.user?.firstName);
      }
      return;
    }

    if (isIntroPlaying) return;
    if (chatId !== currentUrlId || (messages.length === 0 && !isIntroPlaying) || fetchTrigger) {
      fetchChatDataForId(currentUrlId);
      if (fetchTrigger) setFetchTrigger(false);
    }
  }, [
    location,
    authCtx.user,
    fetchTrigger,
    isIntroPlaying,
    introPlayedForThisChat,
    chatId,
    chatSegmentId,
    messages.length,
  ]);

  /* profile picture */
  const [image, setImage] = useState(undefined);
  useEffect(() => {
    (async () => {
      try {
        const res = await api(profilePicture, 'get', null, { responseType: 'blob' });
        setImage(res.data.size === 4 ? null : URL.createObjectURL(res.data));
      } catch (e) {
        console.error('img error', e);
      }
    })();
  }, []);

  /* websocket */
  useEffect(() => {
    // const ws = new WebSocket(`ws://localhost:4005/ws?token=${authCtx.token}`);
    const ws = new WebSocket(`wss://therapywithai.com/ws?token=${authCtx.token}`);
    ws.binaryType = 'arraybuffer';

    ws.onopen = () => console.log('WS connected');
    ws.onmessage = (msg) => {
      if (typeof msg.data === 'string') {
        try {
          const data = JSON.parse(msg.data);
          if (data.type === 'chatId') {
            setChatId(data.chatId);
            setChatSegmentId(data.chatSegmentId);
            localStorage.setItem('primaryChatId', data.chatId);
            navigate('/chat?id=' + data.chatId, { replace: true });
          } else if (data.error) {
            NotificationManagerError(data.error);
            setIsSending(false);
            setDots(false);
            if (currentStreamingIdRef.current) {
              setMessages(prev => prev.map(m => m.id === currentStreamingIdRef.current ? { ...m, isStreaming: false } : m));
              currentStreamingIdRef.current = null;
            }
          } else if (data.type === 'chatResponsePart') {
            setDots(false);
            setMessages((prev) => {
              const existingMessageIndex = prev.findIndex(m => m.id === currentStreamingIdRef.current);
              if (existingMessageIndex !== -1) {
                const updatedMessages = [...prev];
                const updatedMessage = {
                  ...updatedMessages[existingMessageIndex],
                  fullTargetContent: (updatedMessages[existingMessageIndex].fullTargetContent || '') + data.content,
                  isStreaming: true,
                };
                updatedMessages[existingMessageIndex] = updatedMessage;
                return updatedMessages;
              } else {
                const newMsgId = `assistant-${Date.now()}`;
                currentStreamingIdRef.current = newMsgId;
                return [
                  ...prev,
                  {
                    role: 'assistant',
                    content: '',
                    fullTargetContent: data.content,
                    id: newMsgId,
                    isStreaming: true,
                  },
                ];
              }
            });
          } else if (data.type === 'chatResponseDone') {
            setIsSending(false);
            if (currentStreamingIdRef.current) {
              const streamingId = currentStreamingIdRef.current;
              setMessages(prev => prev.map(m => m.id === streamingId ? { ...m, isStreaming: false } : m));
            }
          } else if (data.type === 'dailyMsgLimitReached') {
            setIsdailyLimit(true);
            setIsSending(true);
            setDots(false);
            if (currentStreamingIdRef.current) {
              setMessages(prev => prev.map(m => m.id === currentStreamingIdRef.current ? { ...m, isStreaming: false } : m));
              currentStreamingIdRef.current = null;
            }
            NotificationManagerError(
              <>
                <a
                  href="https://therapywithai.com/plans-pricing"
                  rel="noopener noreferrer"
                  style={{ color: '#fff' }}
                >
                  Our servers are busy. Sign up for Premium for instant access.
                </a>
              </>
            );
          }
        } catch (e) {
          NotificationManagerError('Error processing message.');
        }
      } else if (msg.data instanceof ArrayBuffer && speechEnabled) {
        playAudio(msg.data);
      }
    };
    ws.onerror = (e) => console.error('WS error', e);
    setClient(ws);
    return () => ws.readyState === WebSocket.OPEN && ws.close();
  }, [authCtx.token, speechEnabled, navigate]);

  /* send text */
  const onSend = () => {
    if (isdailyLimit || !myMessage.trim() || isSending) return;

    // ✨ NEW: If AI was streaming and user sends a new message, finalize AI's last message stream.
    if (currentStreamingIdRef.current) {
      const streamingId = currentStreamingIdRef.current;
      setMessages(prev => prev.map(m =>
        m.id === streamingId ? { ...m, isStreaming: false } : m
      ));
      currentStreamingIdRef.current = null;
    }

    setIsSending(true);
    setDots(true);
    const userMessageContent = myMessage; // Capture message content before clearing
    setMessages((prev) => [...prev, { role: 'user', content: userMessageContent, id: `user-${Date.now()}` }]);
    setMyMessage(''); // Clear input after capturing content for the state update

    if (client && client.readyState === WebSocket.OPEN) {
      client.send(
        JSON.stringify({
          type: 'postChat',
          userId: authCtx.user.userId,
          chatId,
          chatSegmentId,
          userInput: userMessageContent, // Use captured content
        })
      );
    } else {
      console.error('WebSocket not open. Cannot send message.');
      setIsSending(false); // Allow user to try again if WS wasn't open
    }
  };

  /* ✨ NEW: useEffect for animating assistant messages */
  useEffect(() => {
    let animationTimeout;

    const animate = () => {
      setMessages(prevMessages => {
        return prevMessages.map(msg => {
          if (msg.role === 'assistant' && !msg.isIntro && msg.fullTargetContent && msg.content.length < msg.fullTargetContent.length) {
            return {
              ...msg,
              content: msg.fullTargetContent.slice(0, msg.content.length + 1),
            };
          }
          return msg;
        });
      });
    };

    // Check if any message needs animation
    const needsAnimation = messages.some(msg =>
      msg.role === 'assistant' && !msg.isIntro && msg.fullTargetContent && msg.content.length < msg.fullTargetContent.length
    );

    if (needsAnimation) {
      animationTimeout = setTimeout(animate, 30);
    }

    return () => clearTimeout(animationTimeout); // Cleanup timeout on unmount or if messages change
  }, [messages]);

  /* ---------- UI ---------- */
  return (
    <>
      <div className="chatRight-bar">
        <div
          className="chat-main"
          ref={chatContainerRef}
          style={{ maxHeight: '600px', overflowY: 'auto' }}
        >
          {messages.map((m, i) => (
            <div
              className={`user-chat-single-list-wrap ${
                m.role === 'user' ? 'client-box' : 'bot-box'
              }`}
              key={i}
            >
              <div className="d-flex align-items-center user-chat-single-list">
                {m.role === 'user' ? (
                  <>
                    <div className="chat-box">{m.content}</div>
                    <img
                      src={image || '/assets/images/ac-user-img.jpg'}
                      alt=""
                      className="img-user"
                      onError={(e) => {
                        e.currentTarget.onerror = null;
                        e.currentTarget.src = '/assets/images/ac-user-img.jpg';
                      }}
                    />
                  </>
                ) : (
                  <>
                    <img
                      src="/assets/images/chat-logo.png"
                      alt=""
                      className="img-user"
                      onError={(e) => {
                        e.currentTarget.onerror = null;
                        e.currentTarget.src = '/assets/images/ac-user-img.jpg';
                      }}
                    />
                    <div className="chat-box">{m.content}</div>
                  </>
                )}
              </div>
            </div>
          ))}
          {dots && (
            <div className="user-chat-single-list-wrap bot-box">
              <div className="d-flex align-items-center user-chat-single-list">
                <img src="/assets/images/chat-logo.png" alt="" className="img-user" />
                <div className="three-dots-wrap">
                  <div id="darkblue" className="position-relative">
                    <span className="loading"></span>
                    <span className="loading"></span>
                    <span className="loading"></span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="typing-chat">
          <div className="d-flex align-items-center chat-typing">
            <input
              type="text"
              className="form-control"
              value={myMessage}
              onChange={(e) => setMyMessage(e.target.value)}
              onKeyUp={(e) => e.key === 'Enter' && onSend()}
              placeholder="Type a message"
              readOnly={isSending || isIntroPlaying}
            />
            <button
              onClick={onSend}
              type="button"
              className="chat-send"
              disabled={isSending || isIntroPlaying || !myMessage.trim()}
            >
              <img src="/assets/images/send.png" alt="" />
            </button>
            <button
              onClick={toggleListening}
              type="button"
              className={`mic-button ${isListening ? 'listening' : ''}`}
              style={{ marginLeft: '5px' }}
              disabled={isIntroPlaying || isSending}
            >
              🎤
            </button>
            <span className={`listening-indicator ${isListening ? 'active' : ''}`}>
              🔴 Listening...
            </span>
            <button
              onClick={toggleSpeech}
              type="button"
              className="toggle-speech"
              style={{ marginLeft: '0px' }}
            >
              <VolumeIcon isMuted={!speechEnabled} />
            </button>
          </div>
        </div>
      </div>

      <style>
        {`
          .mic-button{
            margin: 0 5px;
          }
          .mic-button,
          .toggle-speech {
            padding: 7px;
            font-size: 18px;
            border: 1px solid #ccc;
            border-radius: 50%;
            cursor: pointer;
            background: #fff;
          }
          .mic-button.listening,
          .toggle-speech.enabled {
            color: red;
          }
          .listening-indicator {
            display: none;
            color: red;
            margin-left: 10px;
          }
          .listening-indicator.active {
            display: inline;
          }
            .chat-box {
              margin-left: 12px; 
              margin-right: 12px !important;                      /* consistent spacing from avatar */
            }
                /* 👉 NEW — assistant bubble visual fixes */
    .bot-box .user-chat-single-list .chat-box {
      margin-left: 12px; 
      margin-right: 12px !important;                      /* consistent spacing from avatar */
      background: linear-gradient(90deg, #4656cf 0%, #21a9c7 100%); /* uniform colour */
    }
      
        `}
      </style>
    </>
  );
};

export default ChatRight;
